import React, { useMemo } from 'react'
import { ZoomSettings } from './ZoomSettings'
import { Modal } from '../../../../components/Modal/Modal'
import { Alert, Col, Container, Row, Modal as BoostrapModal } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../store/user/user'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../../store/auth/authSlice'
import { zoomSettingsDefaultValues, ZoomSettingsFormValues } from '../../../Integration/IntegrationManagement/IntegrationSpecificSettings/Zoom/models'
import { ExternalUserServiceIntegrationConfig, ExternalUserServiceIntegrationUpdate } from '@zocks-communications/client-sdk-js'
import { useGetUserActiveSessionsQuery } from '../../../../store/media/media'

import { IntegrationServiceId } from '../../../../common/integrations'

export type ZoomSettingsModalProps = {
  show: boolean
  onHide: () => void
}

export const THREE_HOUR_IN_SECONDS = 3 * 60 * 60
export const ZoomSettingsModal: React.FC<ZoomSettingsModalProps> = ({ show, onHide }) => {
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)!
  const { data: userData, error: userError } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const form = useForm<ZoomSettingsFormValues>({ defaultValues: zoomSettingsDefaultValues as ZoomSettingsFormValues })
  const { handleSubmit } = form
  const [updateUserIntegration, { isLoading: isUpdating, error: integrationUpdateError, isSuccess: isIntegrationSuccess }] = useUpdateUserIntegrationMutation()

  const {
    data: activeSessions,
    isLoading: activeSessionLoading,
    error: activeSessionsError,
  } = useGetUserActiveSessionsQuery(
    {
      accountId: accountId,
      userId: authenticatedUser.id,
      kind: 'external',
      startedAfter: Math.floor(Date.now() / 1000) - THREE_HOUR_IN_SECONDS,
      pageRequest: {
        page: 0,
        size: 10,
      },
    },
    { refetchOnMountOrArgChange: true, skip: !show },
  )

  const meetingInProgress = useMemo(() => {
    if (!activeSessionLoading && activeSessions && show) {
      return activeSessions.data.length > 0
    }
    return false
  }, [activeSessionLoading, show, activeSessions])

  const handleSave = async (data: ZoomSettingsFormValues) => {
    const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ZOOM)
    if (integration) {
      const { integrationType, removeSessionWhenNoRecording, autoJoin } = data
      const payload = {
        configuration: {
          type: integrationType,
          ...(integrationType === 'bot' && { autoJoin }),
          ...(integrationType === 'recording' && { removeSessionWhenNoRecording }),
        } as ExternalUserServiceIntegrationConfig,
      } as ExternalUserServiceIntegrationUpdate

      await updateUserIntegration({
        accountId,
        integrationName: integration.serviceId,
        integrationId: integration.id,
        payload: payload,
      })
      onHide()
    }
  }

  const body =
    !activeSessionLoading && userData && userData.integrations ? (
      <Container>
        <Row>
          <FormProvider {...form}>
            <ZoomSettings integration={userData && userData.integrations?.find((i) => i.serviceId === IntegrationServiceId.ZOOM)} />
          </FormProvider>
        </Row>
        {meetingInProgress && (
          <Row>
            <Col>
              <Alert variant='warning'>You cannot change your Zoom settings while you have an active meeting in progress on Zoom.</Alert>
            </Col>
          </Row>
        )}
      </Container>
    ) : (
      <div className='skeleton' style={{ width: '100%', height: '24px' }} />
    )

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<BoostrapModal.Title>Edit Zoom Integration Settings</BoostrapModal.Title>}
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={handleSubmit(handleSave)}
      footerPrimaryActionDisabled={meetingInProgress}
      footerPrimaryActionDescription={meetingInProgress ? 'You have a meeting in progress on Zoom' : ''}
      footerPrimaryActionLoading={isUpdating}
      centered={true}
    />
  )
}
